import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getLicenseUsage } from '../../../services/licenseService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(229, 57, 53, 0.1);
  color: #E53935;
`;

const WarnBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(245, 158, 11, 0.12);
  color: #F59E0B;
`;

// Kullanım listesi (v1/licenses/usage) sayfalanmaz — kiracı başına modül
// sayısı zaten küçük (bkz. Modül Kodları tablosu, CLAUDE.md). UserGroupsPage
// desenindeki gibi bir `create/update/_delete` yok: bu bir CRUD listesi değil,
// salt-okunur kullanım görünümü + satır aksiyonlarıyla (Kural/Koltuklar)
// açılan ayrı çekmece/alt-tablo.
const useLicenseApi = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    getLicenseUsage()
      .then((res) => {
        // api.js GET okumalarında reject ETMEZ; gerçek başarı sinyali
        // res.data.status'tur — "yüklenemedi" ile "boş" aynı şey değildir
        // ama ikisinde de tablo çökmemeli, boş liste gösterilir.
        if (res.data?.status !== false && Array.isArray(res.data?.data)) {
          setData(res.data.data);
        } else {
          setData([]);
        }
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const columns = useMemo(() => [
    { name: 'moduleId', label: 'ID', options: { display: 'excluded', filter: false, sort: false } },
    {
      name: 'name', label: t('license.module'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    { name: 'code', label: t('license.module') + ' ' + t('common.code'), options: { filter: false, sort: false } },
    {
      name: 'usage', label: t('license.usage'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return '';
          const used = (row.activeSeats || 0) + (row.overageSeats || 0);
          if (row.seatLimit === null || row.seatLimit === undefined) return t('license.unlimited');
          return `${used} / ${row.seatLimit}`;
        },
      },
    },
    {
      name: 'overageSeats', label: t('license.overageSeats'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || !row.overageSeats) return '-';
          return <WarnBadge>{row.overageSeats}</WarnBadge>;
        },
      },
    },
    {
      name: 'blockedSeats', label: t('license.blockedSeats'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || !row.blockedSeats) return '-';
          return <Badge data-testid={`blocked-badge-${row.moduleId}`}>{row.blockedSeats}</Badge>;
        },
      },
    },
    // İ-10 — `clientSide: true` ŞART: bu tablo `serverSide={false}` çalışıyor ve
    // `onTableChanged` HİÇ verilmiyor. Bayrak olmadan `dateFilterColumn`
    // `filterOptions.logic = () => false` kuruyor (filtrelemeyi backend'e devreden mod),
    // yani kullanıcı iki DatePicker'dan tarih seçip "Uygula"ya bastığında afiş çıkıyor
    // ama HİÇBİR satır süzülmüyordu — sahte filtre (bkz. utils/dateFilterColumn.js:19,60).
    dateFilterColumn('contractEndDate', t('license.contractEnd'), lang, { clientSide: true }),
  ], [t, lang, data]);

  return { columns, data, fetching, refetch };
};

export default useLicenseApi;
